import { useShallow } from 'zustand/shallow';
import {
  useAddCredentialsDialogStore,
  type CredentialData,
  type ValidationErrors,
  AddCredentialsDialogStep,
} from './AddCredentialsDialogStore';
import { useTranslations } from 'next-intl';
import { z } from 'zod';

const EmailSchema = z.email();
const UrlSchema = z.url();

export const hasCredentialAuthenticationMethod = (
  credential: CredentialData,
): boolean => {
  const hasUsername = !!credential.username?.trim();
  const hasCompleteBasicAuth =
    credential.basicAuthUsername.trim().length > 0 &&
    credential.basicAuthPassword.trim().length > 0;
  const hasCompleteMutualTls =
    (credential.mtlsPfx.trim().length > 0 ||
      (credential.mtlsCert.trim().length > 0 &&
        credential.mtlsKey.trim().length > 0)) &&
    credential.mtlsOrigin.trim().length > 0;
  const bodySignature = credential.bodySignature;
  const hasCompleteBodySignature =
    bodySignature != null &&
    bodySignature.headerName.trim().length > 0 &&
    bodySignature.secret.trim().length > 0 &&
    bodySignature.algorithm.trim().length > 0;

  return (
    hasUsername ||
    hasCompleteBasicAuth ||
    hasCompleteMutualTls ||
    hasCompleteBodySignature
  );
};

export const useValidateStep = () => {
  const t = useTranslations('designSystem.credentials.addDialog.steps');
  const { credential, formMeta } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      credential: state.credential,
      formMeta: state.formMeta,
    })),
  );

  const validateEmail = (email: string) => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      return t('basicInformation.email.error');
    }

    return EmailSchema.safeParse(normalizedEmail).success
      ? ''
      : t('basicInformation.email.error');
  };

  const validateRole = (role: string) => {
    return !role ? t('basicInformation.role.error') : '';
  };

  const validateTotpSecret = (): string => {
    if (!formMeta.mfaEnabled) {
      return '';
    }

    const otpConfig = formMeta.mfaConfigs.find(
      ({ providerType, enabled }) => providerType === 'OTP' && enabled,
    );

    return otpConfig && !otpConfig.totpSecret?.trim()
      ? t('multiFactorAuthentication.totpSecret.error')
      : '';
  };

  const validateBasicInformationStep = (): Partial<ValidationErrors> => {
    return {
      email:
        formMeta.loginType === 'email' ? validateEmail(formMeta.email) : '',
      role: validateRole(credential.role),
      totpSecret: validateTotpSecret(),
    };
  };

  const validateLoginUrl = (url: string | null | undefined) => {
    if (!url || !url.trim()) {
      return '';
    }

    return UrlSchema.safeParse(url.trim()).success
      ? ''
      : t('loginInformation.loginUrl.error');
  };

  const validateLoginInformationStep = (): Partial<ValidationErrors> => {
    const errors: Partial<ValidationErrors> = {
      loginUrl: validateLoginUrl(credential.loginUrl),
    };

    if (!formMeta.requiresBasicAuth) {
      return { ...errors, basicAuthUsername: '', basicAuthPassword: '' };
    }

    return {
      ...errors,
      basicAuthUsername: credential.basicAuthUsername.trim()
        ? ''
        : t('loginInformation.basicAuth.username.error'),
      basicAuthPassword: credential.basicAuthPassword.trim()
        ? ''
        : t('loginInformation.basicAuth.password.error'),
    };
  };

  const validateRequestAuthenticationStep = (): Partial<ValidationErrors> => {
    const authentication = hasCredentialAuthenticationMethod(credential)
      ? ''
      : t('multiFactorAuthentication.authenticationError');

    if (!formMeta.mtls.enabled) {
      return {
        mtlsOrigin: '',
        mtlsCertificate: '',
        mtlsKey: '',
        authentication,
        totpSecret: validateTotpSecret(),
      };
    }

    const hasCertOrPfx =
      credential.mtlsCert.length > 0 || credential.mtlsPfx.length > 0;

    return {
      mtlsOrigin: credential.mtlsOrigin.trim()
        ? ''
        : t('requestAuthentication.mtls.origin.error'),
      mtlsCertificate: hasCertOrPfx
        ? ''
        : t('requestAuthentication.mtls.certificate.error'),
      mtlsKey:
        credential.mtlsPfx.length === 0 && credential.mtlsKey.length === 0
          ? t('requestAuthentication.mtls.key.error')
          : '',
      authentication,
      totpSecret: validateTotpSecret(),
    };
  };

  const validateStep = (
    step: AddCredentialsDialogStep,
  ): Partial<ValidationErrors> => {
    switch (step) {
      case AddCredentialsDialogStep.BASIC_INFORMATION:
        return validateBasicInformationStep();
      case AddCredentialsDialogStep.LOGIN_INFORMATION:
        return validateLoginInformationStep();
      case AddCredentialsDialogStep.REQUEST_AUTHENTICATION:
        return validateRequestAuthenticationStep();
      default:
        return {};
    }
  };

  return { validateStep };
};
